import React, { useEffect, useState } from "react";
import UpdateProfile from "./UpdateProfile";
import DeleteProfile from "./DeleteProfile";
import {
  Avatar,
  Typography,
  Paper,
  Grid,
  Box,
  Collapse,
  Alert,
  IconButton,
  CircularProgress,
  Chip,
  Card,
  CardContent,
  Divider,
  Stack,
  alpha,
} from "@mui/material";
import { toast } from "react-toastify";
import {
  Person,
  Close as CloseIcon,
  Email,
  Cake,
  School,
  Work,
  LocationOn,
  CalendarToday,
  WbSunny,
  WbTwilight,
  NightsStay,
  TrendingUp,
  MilitaryTech,
  Psychology,
  AutoAwesome,
  Rocket,
  Star,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { setUser } from "../../app-store/userSlice";

const Dashboard = () => {
  const userId = useSelector((state) => state.auth.userId);
  const profile = useSelector((state) => state.user.data);
  const loading = useSelector((state) => state.user.loading);
  const userError = useSelector((state) => state.user.error);
  const userErrorStatus = useSelector((state) => state.user.errorStatus);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    if (!userId) {
      setError("No user ID provided");
    }
  }, [userId]);

  useEffect(() => {
    if (!userError) return;

    if (userErrorStatus === 401) {
      toast.error("Your session has expired! Please log in again.");

      setTimeout(() => {
        navigate("/auth/login", { replace: true });
      }, 3000);
    } else {
      setError(userError);
    }
  }, [userError, userErrorStatus, navigate]);

  const getGreeting = () => {
    const hour = new Date().getHours();

    if (hour >= 0 && hour < 12) {
      return {
        text: "Good Morning",
        icon: <WbSunny sx={{ fontSize: 32 }} />,
        emoji: "🌅",
        color: "#FFB300",
        bgColor: "rgba(255, 179, 0, 0.2)",
      };
    } else if (hour >= 12 && hour < 17) {
      return {
        text: "Good Afternoon",
        icon: <WbTwilight sx={{ fontSize: 32 }} />,
        emoji: "☀️",
        color: "#FF8F00",
        bgColor: "rgba(255, 143, 0, 0.2)",
      };
    } else {
      return {
        text: "Good Evening",
        icon: <NightsStay sx={{ fontSize: 32 }} />,
        emoji: "🌙",
        color: "#6A1B9A",
        bgColor: "rgba(106, 27, 154, 0.15)",
      };
    }
  };

  const getMotivationalMessage = () => {
    const messages = [
      "You're doing great! Keep it up! 🚀",
      "Believe in yourself! 💪",
      "Stay focused and never give up! 🎯",
      "Your journey is inspiring! ✨",
      "Keep pushing forward! 🌟",
      "You're making progress every day! 📈",
      "Success starts with self-belief! 🌈",
    ];

    return messages[Math.floor(Math.random() * messages.length)];
  };

  const getExperienceLevel = (years) => {
    const numYears = parseInt(years);

    if (isNaN(numYears) || numYears < 0) {
      return {
        level: "Not Specified",
        icon: <Person fontSize="small" />,
        color: "#9E9E9E",
        bgColor: "#F5F5F5",
        description: "No experience data available",
      };
    } else if (numYears === 0) {
      return {
        level: "Entry Level",
        icon: <AutoAwesome fontSize="small" />,
        color: "#4CAF50",
        bgColor: "#E8F5E9",
        description: "Starting your professional journey",
      };
    } else if (numYears >= 1 && numYears <= 2) {
      return {
        level: "Junior",
        icon: <Rocket fontSize="small" />,
        color: "#2196F3",
        bgColor: "#E3F2FD",
        description: "Building foundational skills",
      };
    } else if (numYears >= 3 && numYears <= 5) {
      return {
        level: "Intermediate",
        icon: <TrendingUp fontSize="small" />,
        color: "#FF9800",
        bgColor: "#FFF3E0",
        description: "Growing expertise and independence",
      };
    } else if (numYears >= 6 && numYears <= 10) {
      return {
        level: "Senior",
        icon: <MilitaryTech fontSize="small" />,
        color: "#9C27B0",
        bgColor: "#F3E5F5",
        description: "Deep expertise and mentorship",
      };
    } else if (numYears >= 11 && numYears <= 20) {
      return {
        level: "Expert",
        icon: <Psychology fontSize="small" />,
        color: "#E91E63",
        bgColor: "#FCE4EC",
        description: "Mastery and strategic thinking",
      };
    } else {
      return {
        level: "Industry Legend",
        icon: <Star fontSize="small" />,
        color: "#FFD700",
        bgColor: "#FFF8E1",
        description: "Decades of experience and wisdom",
      };
    }
  };

  const greeting = getGreeting();
  const motivationalMessage = getMotivationalMessage();
  const experienceLevel = getExperienceLevel(profile?.experience);

  const profileFields = [
    {
      icon: <Email />,
      label: "Email",
      value: profile?.email,
    },
    {
      icon: <Cake />,
      label: "Age",
      value: profile?.age,
    },
    {
      icon: <School />,
      label: "Degree",
      value: profile?.degree,
    },
    {
      icon: <Work />,
      label: "Experience",
      value: profile?.experience,
    },
    {
      icon: experienceLevel.icon,
      label: "Experience Level",
      value: (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.25,
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontWeight: 500,
              fontSize: {
                xs: "0.85rem",
                sm: "1rem",
              },
            }}
          >
            {experienceLevel.level}
          </Typography>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              lineHeight: 1.4,
              fontSize: {
                xs: "0.7rem",
                sm: "0.75rem",
              },
            }}
          >
            {experienceLevel.description}
          </Typography>
        </Box>
      ),
    },
    {
      icon: <LocationOn />,
      label: "Location",
      value: profile?.location,
    },
  ];

  const getInitials = (name) => {
    if (!name) return "";

    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const handleProfileUpdate = (updatedProfile) => {
    dispatch(setUser(updatedProfile));
    toast.success("Profile updated successfully! 🎉");
  };

  const handleProfileDelete = () => {
    dispatch(setUser(null));
  };

  const renderUpdateTrigger = (onClick) => (
    <button
      style={{
        padding: "8px 18px",
        fontSize: "13px",
        borderRadius: "8px",
        border: "0.5px solid #00786F",
        backgroundColor: "transparent",
        color: "#00786F",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "6px",
        width: "100%",
        fontWeight: 500,
        transition: "all 0.2s ease",
      }}
      onClick={onClick}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = "rgba(0, 120, 111, 0.04)";
        e.currentTarget.style.borderColor = "#005b54";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = "transparent";
        e.currentTarget.style.borderColor = "#00786F";
      }}
    >
      Update Profile
    </button>
  );

  const renderDeleteTrigger = (onClick) => (
    <button
      style={{
        padding: "8px 18px",
        fontSize: "13px",
        borderRadius: "8px",
        border: "0.5px solid #c62828",
        backgroundColor: "transparent",
        color: "#c62828",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "6px",
        width: "100%",
        fontWeight: 500,
        transition: "all 0.2s ease",
      }}
      onClick={onClick}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = "rgba(198, 40, 40, 0.04)";
        e.currentTarget.style.borderColor = "#b71c1c";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = "transparent";
        e.currentTarget.style.borderColor = "#c62828";
      }}
    >
      Delete Account
    </button>
  );

  return (
    <Box
      sx={{
        backgroundColor: "#f5f7fa",
        minHeight: "100vh",
        width: "100%",
        overflow: "hidden",
      }}
    >
      <Typography variant="h4" fontWeight={600} color="#00786F" sx={{ mb: 3 }}>
        Dashboard
      </Typography>

      {/* Error Alert */}
      <Collapse in={Boolean(error)}>
        <Box sx={{ mb: 3 }}>
          <Alert
            severity="error"
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => setError(null)}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
            sx={{
              mb: 2,
              borderRadius: 2,
            }}
          >
            {error}
          </Alert>
        </Box>
      </Collapse>

      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <CircularProgress size={60} />

            <Typography
              sx={{
                mt: 2,
                color: "text.secondary",
              }}
            >
              Loading your profile...
            </Typography>
          </Box>
        </Box>
      ) : profile ? (
        <Grid
          container
          spacing={{
            xs: 2,
            sm: 3,
            md: 4,
          }}
        >
          {/* ========================================= */}
          {/* LEFT COLUMN - PROFILE CARD */}
          {/* ========================================= */}

          <Grid item xs={12} md={4}>
            <Card
              sx={{
                borderRadius: {
                  xs: 3,
                  md: 4,
                },
                boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                position: "relative",
                height: "100%",
                width: "100%",
              }}
            >
              {/* Greeting + Avatar */}
              <Box
                sx={{
                  pt: {
                    xs: 3,
                    sm: 4,
                  },
                  pb: {
                    xs: 2,
                    sm: 3,
                  },
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  background: `linear-gradient(
                    180deg,
                    ${alpha("#00786F", 0.08)} 0%,
                    transparent 100%
                  )`,
                  px: {
                    xs: 2,
                    sm: 3,
                  },
                }}
              >
                <Box
                  sx={{
                    textAlign: "center",
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: "#00786F",
                      lineHeight: 1.2,
                      fontSize: {
                        xs: "1.1rem",
                        sm: "1.25rem",
                      },
                    }}
                  >
                    {greeting.text}, {profile.username}
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mt: 0.5,
                      fontSize: {
                        xs: "0.8rem",
                        sm: "0.875rem",
                      },
                    }}
                  >
                    {greeting.emoji}{" "}
                    {greeting.text === "Good Morning"
                      ? "Rise and shine!"
                      : greeting.text === "Good Afternoon"
                        ? "Hope your day is going well."
                        : "Winding down nicely?"}
                  </Typography>
                </Box>

                <Avatar
                  src={profile.image_url}
                  alt={profile.username}
                  sx={{
                    width: {
                      xs: 90,
                      sm: 110,
                    },
                    height: {
                      xs: 90,
                      sm: 110,
                    },
                    border: "4px solid white",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                    bgcolor: "#00786F",
                  }}
                >
                  {!profile.image_url && getInitials(profile.username)}
                </Avatar>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    mt: 1.5,
                  }}
                >
                  <CalendarToday
                    fontSize="small"
                    sx={{
                      fontSize: 14,
                      color: "text.secondary",
                    }}
                  />

                  <Typography variant="caption" color="text.secondary">
                    Member since {new Date().getFullYear()}
                  </Typography>
                </Box>
              </Box>

              <CardContent
                sx={{
                  pt: {
                    xs: 1,
                    sm: 2,
                  },
                  pb: {
                    xs: 2,
                    sm: 3,
                  },
                  px: {
                    xs: 2,
                    sm: 3,
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    mb: 2,
                  }}
                >
                  <Chip
                    icon={experienceLevel.icon}
                    label={experienceLevel.level}
                    size="small"
                    sx={{
                      bgcolor: experienceLevel.bgColor,
                      color: experienceLevel.color,
                      fontWeight: 600,
                      px: 1,
                      py: 0.5,
                      height: "auto",
                      "& .MuiChip-icon": {
                        color: experienceLevel.color,
                      },
                      fontSize: {
                        xs: "0.7rem",
                        sm: "0.8rem",
                      },
                    }}
                  />
                </Box>

                {/* Motivational Message */}
                <Box
                  sx={{
                    textAlign: "center",
                    mb: 2,
                  }}
                >
                  <Chip
                    label={motivationalMessage}
                    size="small"
                    sx={{
                      bgcolor: alpha("#00786F", 0.08),
                      maxWidth: "100%",
                      height: "auto",
                      fontStyle: "italic",
                      "& .MuiChip-label": {
                        whiteSpace: "normal",
                        py: 0.5,
                        lineHeight: 1.4,
                        fontSize: {
                          xs: "0.7rem",
                          sm: "0.8rem",
                        },
                      },
                    }}
                  />
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Quick Actions */}
                <Box sx={{ mt: 2 }}>
                  <Stack spacing={1.5}>
                    <UpdateProfile
                      profile={profile}
                      onUpdate={handleProfileUpdate}
                      renderTrigger={renderUpdateTrigger}
                    />

                    <DeleteProfile
                      profile={profile}
                      onDelete={handleProfileDelete}
                      renderTrigger={renderDeleteTrigger}
                    />
                  </Stack>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* ========================================= */}
          {/* RIGHT COLUMN - PROFILE DETAILS */}
          {/* ========================================= */}

          <Grid item xs={12} md={8}>
            <Card
              sx={{
                borderRadius: {
                  xs: 3,
                  md: 4,
                },
                boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                height: "100%",
                width: "100%",
              }}
            >
              <CardContent
                sx={{
                  p: {
                    xs: 2,
                    sm: 3,
                    md: 4,
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: {
                      xs: 2,
                      sm: 3,
                      md: 4,
                    },
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      color: "#00786F",
                      fontSize: {
                        sm: "1.5rem",
                      },
                    }}
                  >
                    Profile Summary
                  </Typography>
                </Box>

                <Grid
                  container
                  spacing={{
                    xs: 1.5,
                    sm: 2,
                    md: 3,
                  }}
                >
                  {profileFields.map((field, index) => {
                    const isEmail = field.label === "Email";
                    const isLocation = field.label === "Location";

                    return (
                      <Grid
                        item
                        key={index}
                        xs={12}
                        sm={isEmail || isLocation ? 12 : 6}
                        md={isEmail || isLocation ? 12 : 6}
                        lg={6}
                      >
                        <Paper
                          elevation={0}
                          sx={{
                            p: {
                              xs: 1.5,
                              sm: 2,
                              md: 2.5,
                            },
                            backgroundColor: alpha("#00786F", 0.04),
                            borderRadius: 2,
                            height: "100%",
                            width: "100%",
                            boxSizing: "border-box",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: {
                                xs: 1,
                                sm: 1.5,
                              },
                              minWidth: 0,
                            }}
                          >
                            {/* Field Icon */}
                            <Box
                              sx={{
                                p: {
                                  xs: 0.75,
                                  sm: 1,
                                },
                                borderRadius: "50%",
                                backgroundColor: alpha("#00786F", 0.12),
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#00786F",
                                flexShrink: 0,
                              }}
                            >
                              {field.icon}
                            </Box>

                            {/* Field Content */}
                            <Box
                              sx={{
                                flex: 1,
                                minWidth: 0,
                                overflow: "hidden",
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  display: "block",
                                  fontSize: {
                                    xs: "0.65rem",
                                    sm: "0.75rem",
                                  },
                                }}
                              >
                                {field.label}
                              </Typography>

                              {typeof field.value === "string" ? (
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontWeight: 500,
                                    fontSize: {
                                      xs: "0.85rem",
                                      sm: "1rem",
                                    },
                                    overflowWrap: "anywhere",
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {field.value || "Not specified"}
                                </Typography>
                              ) : (
                                field.value
                              )}
                            </Box>
                          </Box>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ) : (
        <Box
          sx={{
            textAlign: "center",
            py: 8,
          }}
        >
          <Typography variant="h6" color="text.secondary">
            No profile data available.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default Dashboard;
